/**
 * Romanian audio playback — driven by the on-demand TTS service.
 *
 * URL shape: <VITE_AUDIO_URL>/v1/audio?text=<encoded>
 *
 * The service materialises (or returns from cache) an MP3. Browser HTTP cache
 * + the SW audio cache layer make repeats free. Failure modes we explicitly
 * handle:
 *
 *   - 429 (rate-limited): swallow silently. The audio is a "nice-to-have"; a
 *     toast popping up mid-flashcard would be more annoying than the missing
 *     sound.
 *   - 502 (Azure flap): swallow silently. Same reason; will likely succeed on
 *     the next card.
 *   - Network error: same.
 *
 * We never block the UI on audio: every call is fire-and-forget from the
 * caller's POV.
 */

import { useCallback, useEffect, useRef } from "react";

// In dev VITE_AUDIO_URL is unset → empty base means we fall through to the
// Vite proxy (added in vite.config.ts). In prod it's the absolute URL of the
// audio service (e.g. https://audio.rmnlng.com).
const AUDIO_BASE = import.meta.env.VITE_AUDIO_URL ?? "";

export function audioUrl(text: string): string {
  // The trailing path is fixed; only the text changes per call.
  return `${AUDIO_BASE}/v1/audio?text=${encodeURIComponent(text)}`;
}

/**
 * Hook: returns an idempotent `play(text)` that plays Romanian audio for
 * `text`. Auto-plays on mount whenever `autoPlay` is provided.
 *
 *   - `autoPlay`: when set, the hook plays the text on mount AND whenever
 *     the text value changes. Pass `null` (or "") to disable auto-play
 *     without unmounting the hook (e.g., before reveal in RU→RO mode).
 *   - Returns `play(text?)` for manual replay buttons. Defaults to the
 *     current `autoPlay` text if omitted.
 *
 * Why a single shared <audio> element per hook instance: stops overlapping
 * playback if the user mashes the replay button. The previous play is
 * cancelled by setting `src` again.
 */
export function useRomanianAudio(autoPlay?: string | null): {
  play: (text?: string) => void;
} {
  // One <audio> element per hook instance. Lazily created so SSR-style
  // pre-rendering (or test environments without DOM) doesn't blow up.
  const elRef = useRef<HTMLAudioElement | null>(null);

  // Track the current playable text. Always overwritten with the latest
  // `autoPlay` value (including null) so the replay button never plays a
  // stale word from a previous card after navigation.
  const lastTextRef = useRef<string | null>(autoPlay ?? null);
  lastTextRef.current = autoPlay ?? null;

  const play = useCallback((text?: string) => {
    const target = text ?? lastTextRef.current;
    if (!target) return;
    if (!elRef.current) {
      elRef.current = new Audio();
      // Don't preload the previous src on every navigation.
      elRef.current.preload = "auto";
    }
    const el = elRef.current;
    el.src = audioUrl(target);
    // `play()` returns a Promise that rejects on autoplay-blocked or 4xx
    // status. We swallow because the audio is non-essential. The most common
    // rejection is `NotAllowedError` (Safari requires a user gesture before
    // the FIRST audio play in a session) — once the user taps anywhere, the
    // restriction lifts and subsequent auto-plays just work.
    el.play().catch(() => {
      /* intentional — see comment above */
    });
  }, []);

  // Auto-play on mount + on text change. Skips if `autoPlay` is null/empty
  // so callers can gate it (e.g., delay until a flip card is revealed).
  useEffect(() => {
    if (!autoPlay) return;
    play(autoPlay);
  }, [autoPlay, play]);

  // Stop any in-flight playback when the hook unmounts.
  useEffect(() => {
    return () => {
      const el = elRef.current;
      if (el) {
        el.pause();
        el.src = "";
      }
    };
  }, []);

  return { play };
}
